import { DesktopBrowserLogin } from '@/components/DesktopBrowserLogin'
import { isDesktop } from '@/lib/desktop'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useVerifyStatus } from '@/lib/useVerifyStatus'
import { clearReferral, parseReferral, referralCode, saveReferral } from '@/lib/referral'
import { trackMarketingPage, trackMarketingGoal, stopMetrika } from '@/lib/metrika'
import { campaign, trackVisit } from '@/lib/campaign'
import { ApiError } from '@/lib/api'
import { CODE_MFA_REQUIRED, authApi, describeError, type PhoneStart } from '@/lib/auth'
import { useAuthStore } from '@/store/auth'
import { cx } from '@/lib/cx'
import { testCode } from '@/lib/stage'
import { formatPhoneRu, phoneNationalDigits } from '@/lib/phone'

/** Поле входа принимает и номер, и почту: форматируем как номер, только пока это похоже на номер. */
const identifierInput = (v: string): string => (/^[\d\s+()-]*$/.test(v) ? formatPhoneRu(v) : v)
/** Кнопка входа: для номера — десять цифр, для почты — что-то похожее на адрес. */
const identifierComplete = (v: string): boolean => (v.includes('@') ? /^\S+@\S+\.\S+$/.test(v.trim()) : phoneNationalDigits(v).length >= 10)
import { captchaEnabled, solveCaptcha } from '@/lib/captcha'
import { DesktopTitlebar } from '@/components/DesktopTitlebar'
import { CodeFallback } from '@/components/CodeFallback'
import { useAppStore } from '@/store/app'
import { guildApi } from '@/lib/guildApi'
import { guildIconUrl } from '@/lib/avatar'
import { guildColor, guildInitials, plural } from '@/lib/guildUi'
import type { Invite } from '@/types/api'
import styles from './AuthPage.module.css'
import { LINKS } from '@/lib/links'

type Mode = 'login' | 'register' | 'recover'
type Step = 'phone' | 'email' | 'waiting' | 'code' | 'profile'

interface FieldErrors {
  [field: string]: string | undefined
}

/**
 * Экран входа и регистрации по номеру телефона. Шаги регистрации:
 * номер → код из SMS → имя, пароль, дата рождения и согласие.
 */
export function AuthPage({ initial = 'login', onHome, onCatalog, desktopApproval = false }: { initial?: Mode; onHome?: () => void; onCatalog?: () => void; desktopApproval?: boolean }) {
  const [mode, setMode] = useState<Mode>(initial)
  // заходы на экраны входа и регистрации считаем сами — по одному разу за загрузку
  useEffect(() => {
    if (desktopApproval) return
    trackVisit(mode === 'register' ? '/register' : mode === 'login' ? '/login' : '')
    if (mode === 'recover') stopMetrika()
    else trackMarketingPage(mode === 'register' ? '/register' : '/login')
  }, [mode, desktopApproval])
  const invite = useAppStore((s) => s.pendingInvite)
  return (
    <div className={styles.page}>
      <DesktopTitlebar />
      <div className={styles.card}>
        {onHome ? <button type="button" className={styles.back} onClick={onHome}>← На главную</button> : null}
        {onCatalog ? <button type="button" className={styles.back} onClick={onCatalog}>Каталог серверов →</button> : null}
        <img className={styles.brand} src="/brand/mark.png" alt="Кличат" width={56} height={56} />
        {invite ? <InviteBanner code={invite} onRegister={() => setMode('register')} /> : null}
        {mode === 'login' ? (
          <LoginForm onSwitch={() => setMode('register')} onRecover={() => setMode('recover')} />
        ) : mode === 'register' ? (
          <RegisterFlow onSwitch={() => setMode('login')} />
        ) : (
          <RecoverFlow onBack={() => setMode('login')} />
        )}
        <p className={styles.support}>
          Не получается войти? <a className={styles.link} href={LINKS.support} target="_blank" rel="noopener">Напишите в поддержку</a>
        </p>
      </div>
    </div>
  )
}

/** Пришли по приглашению и ещё не вошли: показываем, куда зовут, до формы входа. */
function InviteBanner({ code, onRegister }: { code: string; onRegister: () => void }) {
  const [invite, setInvite] = useState<Invite | null>(null)
  useEffect(() => {
    let cancelled = false
    void guildApi
      .invite(code)
      .then((i) => {
        if (!cancelled) setInvite(i)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [code])
  if (!invite) return null
  const members = invite.approximate_member_count ?? 0
  return (
    <div className={styles.invite}>
      <span className={styles.inviteIcon} style={guildIconUrl(invite.guild.id, invite.guild.icon) ? undefined : { background: guildColor(invite.guild.id) }}>
        {guildIconUrl(invite.guild.id, invite.guild.icon) ? <img src={guildIconUrl(invite.guild.id, invite.guild.icon)} alt="" /> : guildInitials(invite.guild.name)}
      </span>
      <div>
        <p className={styles.inviteLead}>Вас пригласили на сервер</p>
        <p className={styles.inviteName}>{invite.guild.name}</p>
        <p className={styles.inviteMeta}>
          {members > 0 ? `${members.toLocaleString('ru-RU')} ${plural(members, 'участник', 'участника', 'участников')}` : 'Новый сервер'}
          {invite.guild.description ? ` · ${invite.guild.description}` : ''}
        </p>
        <button type="button" className={styles.inviteButton} onClick={onRegister}>Создать аккаунт и войти</button>
      </div>
    </div>
  )
}

function LoginForm({ onSwitch, onRecover }: { onSwitch: () => void; onRecover: () => void }) {
  const setSession = useAuthStore((s) => s.setSession)
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [mfa, setMfa] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<FieldErrors>({})

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setFields({})
    try {
      setSession(await authApi.login(phone, password, mfa ? code.trim() : undefined))
    } catch (err) {
      if (err instanceof ApiError && err.code === CODE_MFA_REQUIRED) {
        setMfa(true)
        setCode('')
      } else {
        const d = describeError(err)
        setError(d.message)
        setFields(d.fields)
      }
    } finally {
      setBusy(false)
    }
  }

  if (mfa) {
    return (
      <form className={styles.form} onSubmit={submit} noValidate>
        <h1 className={styles.title}>Двухфакторная аутентификация</h1>
        <p className={styles.subtitle}>Введите код из приложения-аутентификатора или один из резервных кодов.</p>
        <Field label="Код" error={fields.code} htmlFor="login-mfa">
          <input id="login-mfa" className={styles.input} inputMode="text" autoComplete="one-time-code" spellCheck={false} placeholder="123456 или xxxxxxxx-xxxx" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        </Field>
        {error && !fields.code ? <p className={styles.error} role="alert">{error}</p> : null}
        <button type="submit" className={styles.primary} disabled={busy || code.trim().length < 6}>
          {busy ? 'Проверяем…' : 'Войти'}
        </button>
        <p className={styles.footer}>
          <button type="button" className={styles.link} onClick={() => { setMfa(false); setError(null); setFields({}) }}>Назад ко входу</button>
        </p>
      </form>
    )
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <h1 className={styles.title}>Вход</h1>
      {isDesktop ? <DesktopBrowserLogin /> : null}
      <Field label="Номер телефона или почта" error={fields.phone ?? fields.email} htmlFor="login-phone">
        <input id="login-phone" className={styles.input} type="text" autoComplete="username" placeholder="+7 900 000-00-00 или name@example.com" value={phone} onChange={(e) => setPhone(identifierInput(e.target.value))} required />
      </Field>
      <Field label="Пароль" error={fields.password} htmlFor="login-password">
        <input id="login-password" className={styles.input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </Field>
      <button type="button" className={styles.link} onClick={onRecover}>Забыли пароль?</button>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <button type="submit" className={styles.primary} disabled={busy || !identifierComplete(phone) || !password}>
        {busy ? 'Входим…' : 'Войти'}
      </button>
      <p className={styles.footer}>
        Нужна учётная запись?{' '}
        <button type="button" className={styles.link} onClick={onSwitch}>Зарегистрироваться</button>
      </p>
    </form>
  )
}

/**
 * Отправка кода с капчей: сервер требует токен со второй попытки с адреса. Решаем капчу
 * и повторяем запрос один раз; если капча в сборке не настроена — отдаём исходную ошибку.
 */
async function withCaptcha(container: HTMLDivElement | null, send: (token?: string) => Promise<void>): Promise<void> {
  try {
    await send()
  } catch (err) {
    const needs = describeError(err).fields.captcha_token
    if (!needs) throw err
    /*
     * Сервер просит капчу, а показать её нечем: ключ виджета не попал в сборку.
     * Пробрасывать эту ошибку дальше нельзя — поля captcha_token на форме нет, и человек
     * увидит «проверьте выделенные поля», где выделять нечего.
     */
    if (!captchaEnabled || !container) {
      throw new ApiError(400, 0, 'Проверка «я не робот» сейчас недоступна. Попробуйте позже или напишите в поддержку.')
    }
    await send(await solveCaptcha(container))
  }
}

function RegisterFlow({ onSwitch }: { onSwitch: () => void }) {
  const setSession = useAuthStore((s) => s.setSession)
  const [step, setStep] = useState<Step>('phone')
  // чем подтверждаемся: номер — как обычно; почта — для тех, кто не из России (решает сервер)
  const [via, setVia] = useState<'phone' | 'email'>('phone')
  const [regEmail, setRegEmail] = useState('')
  const methods = useQuery({ queryKey: ['auth-methods'], queryFn: authApi.methods, staleTime: 5 * 60_000, retry: 1 })
  const emailAllowed = methods.data?.email === true
  // человек сам выбрал способ — больше не переключаем за него
  const [chosen, setChosen] = useState(false)
  /*
   * Не из России — сразу почта: российского номера у такого человека, скорее всего, нет, и начинать
   * с номера значит заставлять искать ссылку внизу. Номер остаётся второй ссылкой.
   */
  useEffect(() => {
    if (!chosen && emailAllowed && step === 'phone') {
      setVia('email')
      setStep('email')
    }
  }, [chosen, emailAllowed, step])
  const choose = (next: 'phone' | 'email') => {
    setChosen(true)
    setVia(next)
    setError(null)
    setStep(next)
  }
  const [phone, setPhone] = useState('')
  const [start, setStart] = useState<PhoneStart | null>(null)
  const [code, setCode] = useState('')
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [referral, setReferral] = useState(referralCode)
  const [password, setPassword] = useState('')
  const [dob, setDob] = useState('')
  const [email, setEmail] = useState('')
  const [consent, setConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<FieldErrors>({})
  const [resendIn, setResendIn] = useState(0)
  const captchaBox = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    setFields({})
    try {
      await fn()
    } catch (err) {
      const d = describeError(err)
      setError(d.message)
      setFields(d.fields)
    } finally {
      setBusy(false)
    }
  }

  /*
   * При мобильной авторизации номер подтверждает оператор: ok — уже подтверждён и код
   * не нужен вовсе, pending — ждём ответа оператора, needs_code — как раньше, по SMS.
   */
  const stepFor = (st: PhoneStart['status']): Step => (st === 'ok' ? 'profile' : st === 'pending' ? 'waiting' : 'code')

  const sendCode = () =>
    run(() =>
      withCaptcha(captchaBox.current, async (token) => {
        const res = via === 'email' ? await authApi.startEmailRegistration(regEmail.trim(), token) : await authApi.startRegistration(phone, token)
        setStart(res)
        setCode('')
        setResendIn(60)
        setStep(via === 'email' ? 'code' : stepFor(res.status))
      }),
    )
  const backToStart = () => setStep(via === 'email' ? 'email' : 'phone')

  const verifyState = useVerifyStatus(start, 'register')
  const { status, codeLength } = verifyState
  /*
   * Подтверждение от оператора принимаем и с экрана ввода кода: туда можно уйти руками,
   * не дождавшись уведомления, — и остаться ждать код, которого при бесшовном входе
   * не будет вовсе.
   */
  useEffect(() => {
    if (step !== 'waiting' && step !== 'code') return
    if (status === 'ok') setStep('profile')
    else if (step !== 'waiting') return
    else if (status === 'needs_code') setStep('code')
    else if (status === 'failed') {
      setStep('code')
      setError('Подтвердить номер не удалось. Запросите код ещё раз.')
    }
  }, [status, step])

  const submitCode = (e: FormEvent) => {
    e.preventDefault()
    if (!start || code.length !== codeLength) return
    // код проверяем здесь же: иначе опечатку в нём человек увидит, только заполнив ник и пароль
    void run(async () => {
      await authApi.checkCode(start.verification_id, 'register', code)
      setStep('profile')
    })
  }

  const submitProfile = (e: FormEvent) => {
    e.preventDefault()
    if (!start) return
    const invitation = parseReferral(referral)
    if (referral.trim() && !invitation) {
      setFields({ referral_code: 'Введите код или реферальную ссылку.' })
      return
    }
    void run(async () => {
      const pair = await authApi.completeRegistration({
        verification_id: start.verification_id,
        code,
        username,
        display_name: displayName || undefined,
        password,
        date_of_birth: dob,
        consent,
        ...(via !== 'email' && email.trim() ? { email: email.trim() } : {}),
        ...(invitation ? { referral_code: invitation } : {}),
        ...(campaign() ? { campaign: campaign() } : {}),
      })
      clearReferral()
      trackMarketingGoal('registration_success')
      setSession(pair)
    })
  }

  if (step === 'phone' && !chosen && methods.isPending) {
    return (
      <div className={styles.form}>
        <h1 className={styles.title}>Создать учётную запись</h1>
        <p className={styles.muted} aria-live="polite">Секунду…</p>
      </div>
    )
  }

  if (step === 'phone') {
    return (
      <form className={styles.form} onSubmit={(e) => { e.preventDefault(); void sendCode() }} noValidate>
        <h1 className={styles.title}>Создать учётную запись</h1>
        <p className={styles.subtitle}>Пришлём код подтверждения по SMS. Пока принимаем только российские номера.</p>
        <Field label="Номер телефона" error={fields.phone} htmlFor="reg-phone">
          <input id="reg-phone" className={styles.input} type="tel" inputMode="tel" autoComplete="tel" placeholder="+7 900 000-00-00" value={phone} onChange={(e) => setPhone(formatPhoneRu(e.target.value))} required autoFocus />
        </Field>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <div ref={captchaBox} className={styles.captcha} />
        <button type="submit" className={styles.primary} disabled={busy || phoneNationalDigits(phone).length < 10}>
          {busy ? 'Отправляем…' : 'Получить код'}
        </button>
        <p className={styles.footer}>
          {emailAllowed ? (
            <>
              <button type="button" className={styles.link} onClick={() => choose('email')}>По почте</button>
              {' · '}
            </>
          ) : null}
          <button type="button" className={styles.link} onClick={onSwitch}>Уже есть учётная запись?</button>
        </p>
      </form>
    )
  }

  if (step === 'email') {
    return (
      <form className={styles.form} onSubmit={(e) => { e.preventDefault(); void sendCode() }} noValidate>
        <h1 className={styles.title}>Создать учётную запись</h1>
        <p className={styles.subtitle}>Пришлём код на почту. Этот адрес — для входа.</p>
        <Field label="Почта" error={fields.email} htmlFor="reg-email-start">
          <input id="reg-email-start" className={styles.input} type="email" inputMode="email" autoComplete="email" spellCheck={false} placeholder="name@example.com" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} maxLength={254} required autoFocus />
        </Field>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <div ref={captchaBox} className={styles.captcha} />
        <button type="submit" className={styles.primary} disabled={busy || !regEmail.includes('@')}>
          {busy ? 'Отправляем…' : 'Получить код'}
        </button>
        <p className={styles.footer}>
          <button type="button" className={styles.link} onClick={() => choose('phone')}>По номеру телефона</button>
          {' · '}
          <button type="button" className={styles.link} onClick={onSwitch}>Уже есть учётная запись?</button>
        </p>
      </form>
    )
  }

  if (step === 'waiting') {
    return (
      <div className={styles.form}>
        <h1 className={styles.title}>Подтвердите номер</h1>
        <p className={styles.subtitle}>
          Оператор подтверждает {start?.phone ?? phone} сам — если придёт уведомление, нажмите в нём «Подтвердить».
          Код вводить не нужно.
        </p>
        <p className={styles.muted} aria-live="polite">Ждём ответа…</p>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <button type="button" className={styles.primary} onClick={() => setStep('code')}>Ввести код из SMS</button>
        <p className={styles.footer}>
          <button type="button" className={styles.link} onClick={() => setStep('phone')}>Изменить номер</button>
        </p>
        <div ref={captchaBox} className={styles.captcha} />
      </div>
    )
  }

  if (step === 'code') {
    return (
      <form className={styles.form} onSubmit={submitCode} noValidate>
        <h1 className={styles.title}>Введите код</h1>
        <p className={styles.subtitle}>
          Отправили код {via === 'email' ? `на почту ${start?.email ?? regEmail}` : `на ${start?.phone ?? phone}`}
          {testCode ? '' : start?.channel === 'console' ? ' (режим разработки: код в логе сервера)' : ''}.
          {via === 'email' ? ' Если письма нет — загляните в спам.' : ''}
          {/* код присылает оператор, и отправитель у него свой: у МТС это «МТС ID» */}
          {verifyState.channel === 'mobileid' ? ' Отправителем будет ваш оператор, а не Кличат.' : ''}
        </p>
        {testCode ? <p className={styles.testCode}>Тестовый сервер: код всегда {testCode}</p> : null}
        <Field label={via === 'email' ? 'Код из письма' : 'Код из SMS'} error={fields.code} htmlFor="reg-code">
          <CodeInput id="reg-code" value={code} onChange={setCode} length={codeLength} />
        </Field>
        <CodeFallback state={verifyState} />
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <button type="submit" className={styles.primary} disabled={busy || code.length !== codeLength}>Продолжить</button>
        <p className={styles.footer}>
          {resendIn > 0 ? (
            <span className={styles.muted}>Отправить ещё раз можно через {resendIn} с</span>
          ) : (
            <button type="button" className={styles.link} onClick={() => void sendCode()} disabled={busy}>Отправить код ещё раз</button>
          )}
          {' · '}
          <button type="button" className={styles.link} onClick={backToStart}>{via === 'email' ? 'Изменить адрес' : 'Изменить номер'}</button>
        </p>
        <div ref={captchaBox} className={styles.captcha} />
      </form>
    )
  }

  return (
    <form className={styles.form} onSubmit={submitProfile} noValidate>
      <h1 className={styles.title}>Почти готово</h1>
      <Field label="Имя пользователя" hint="Уникальное, латиница, цифры, точка и подчёркивание" error={fields.username} htmlFor="reg-username">
        <input id="reg-username" className={styles.input} autoComplete="username" spellCheck={false} value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} required autoFocus />
      </Field>
      <Field label="Отображаемое имя" hint="Как вас будут видеть другие; можно менять" htmlFor="reg-display">
        <input id="reg-display" className={styles.input} autoComplete="nickname" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={32} />
      </Field>
      <Field label="Пароль" hint="Не короче 8 символов" error={fields.password} htmlFor="reg-password">
        <input id="reg-password" className={styles.input} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </Field>
      <Field label="Дата рождения" error={fields.date_of_birth} htmlFor="reg-dob">
        <input id="reg-dob" className={styles.input} type="date" autoComplete="bday" value={dob} onChange={(e) => setDob(e.target.value)} max={maxBirthDate()} required />
      </Field>
      {/* почта не обязательна и не заменяет номер: она нужна, чтобы не потерять аккаунт при смене номера.
          При регистрации по почте адрес уже подтверждён — второй раз не спрашиваем */}
      {via === 'email' ? null : (
        <Field label="Почта" hint="Необязательно. Пригодится, если смените номер: пришлём письмо для подтверждения" error={fields.email} htmlFor="reg-email">
          <input id="reg-email" className={styles.input} type="email" inputMode="email" autoComplete="email" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} />
        </Field>
      )}
      <Field label="Код приглашения" hint="Необязательно. Можно вставить реферальную ссылку" error={fields.referral_code} htmlFor="reg-referral">
        <input id="reg-referral" className={styles.input} autoComplete="off" autoCapitalize="none" spellCheck={false} value={referral} onChange={(e) => {
          const value = e.target.value
          setReferral(value)
          setFields((old) => ({ ...old, referral_code: undefined }))
          saveReferral(value)
        }} maxLength={512} />
      </Field>
      <label className={cx(styles.checkbox, fields.consent && styles.checkboxError)}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>
          Я принимаю <a href="/legal/terms" target="_blank" rel="noreferrer">Пользовательское соглашение</a> и даю{' '}
          <a href="/legal/consent" target="_blank" rel="noreferrer">согласие на обработку персональных данных</a>. Мне исполнилось 14 лет.
        </span>
      </label>
      {fields.code ? <p className={styles.error} role="alert">{fields.code} <button type="button" className={styles.link} onClick={() => setStep('code')}>Ввести код заново</button></p> : null}
      {error && !fields.code ? <p className={styles.error} role="alert">{error}</p> : null}
      <button type="submit" className={styles.primary} disabled={busy || !consent || !username || !password || !dob}>
        {busy ? 'Создаём…' : 'Создать учётную запись'}
      </button>
    </form>
  )
}

function RecoverFlow({ onBack }: { onBack: () => void }) {
  const setSession = useAuthStore((s) => s.setSession)
  const [step, setStep] = useState<'phone' | 'waiting' | 'code' | 'password' | 'email' | 'email-sent'>('phone')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [start, setStart] = useState<PhoneStart | null>(null)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<FieldErrors>({})
  const [resendIn, setResendIn] = useState(0)
  const captchaBox = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    setFields({})
    try {
      await fn()
    } catch (err) {
      const d = describeError(err)
      setError(d.message)
      setFields(d.fields)
    } finally {
      setBusy(false)
    }
  }

  const sendCode = () =>
    run(() =>
      withCaptcha(captchaBox.current, async (token) => {
        const res = await authApi.startRecovery(phone, token)
        setStart(res)
        setCode('')
        setResendIn(60)
        setStep(res.status === 'ok' ? 'password' : res.status === 'pending' ? 'waiting' : 'code')
      }),
    )

  // нет доступа к номеру — письмо со ссылкой на подтверждённую почту
  const sendEmail = (address: string) =>
    run(() =>
      withCaptcha(captchaBox.current, async (token) => {
        setEmail(address)
        await authApi.recoverByEmail(address.trim(), token)
        setStep('email-sent')
      }),
    )

  const recState = useVerifyStatus(start, 'recover')
  const { status, codeLength: recCodeLength } = recState
  // как и при регистрации: подтверждение от оператора принимаем и с экрана ввода кода
  useEffect(() => {
    if (step !== 'waiting' && step !== 'code') return
    if (status === 'ok') setStep('password')
    else if (step !== 'waiting') return
    else if (status === 'needs_code') setStep('code')
    else if (status === 'failed') {
      setStep('code')
      setError('Подтвердить номер не удалось. Запросите код ещё раз.')
    }
  }, [status, step])

  // как при регистрации: код проверяем на его шаге, а не после нового пароля
  const submitRecoveryCode = (e: FormEvent) => {
    e.preventDefault()
    if (!start || code.length !== recCodeLength) return
    void run(async () => {
      await authApi.checkCode(start.verification_id, 'recover', code)
      setStep('password')
    })
  }

  const finish = (e: FormEvent) => {
    e.preventDefault()
    if (!start) return
    void run(async () => {
      setSession(await authApi.completeRecovery({ verification_id: start.verification_id, code, new_password: password }))
    })
  }

  if (step === 'phone') {
    return (
      <form className={styles.form} onSubmit={(e) => { e.preventDefault(); if (phone.includes('@')) void sendEmail(phone); else void sendCode() }} noValidate>
        <h1 className={styles.title}>Восстановление пароля</h1>
        <p className={styles.subtitle}>На номер пришлём код, на почту — письмо со ссылкой.</p>
        <Field label="Номер телефона или почта" error={fields.phone ?? fields.email} htmlFor="rec-phone">
          <input id="rec-phone" className={styles.input} type="text" autoComplete="username" placeholder="+7 900 000-00-00 или name@example.com" value={phone} onChange={(e) => setPhone(identifierInput(e.target.value))} required autoFocus />
        </Field>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <div ref={captchaBox} className={styles.captcha} />
        <button type="submit" className={styles.primary} disabled={busy || !identifierComplete(phone)}>
          {busy ? 'Отправляем…' : phone.includes('@') ? 'Отправить письмо' : 'Получить код'}
        </button>
        <p className={styles.footer}>
          <button type="button" className={styles.link} onClick={onBack}>Вернуться ко входу</button>
        </p>
      </form>
    )
  }

  if (step === 'email-sent') {
    return (
      <div className={styles.form}>
        <h1 className={styles.title}>Проверьте почту</h1>
        <p className={styles.subtitle}>
          Если {email.trim()} привязана к учётной записи, письмо уже в пути. Ссылка в нём действует час.
          Не пришло — загляните в «Спам».
        </p>
        <button type="button" className={styles.primary} onClick={onBack}>Вернуться ко входу</button>
      </div>
    )
  }

  if (step === 'waiting') {
    return (
      <div className={styles.form}>
        <h1 className={styles.title}>Подтвердите номер</h1>
        <p className={styles.subtitle}>
          Оператор подтверждает {start?.phone ?? phone} сам — если придёт уведомление, нажмите в нём «Подтвердить».
          Код вводить не нужно.
        </p>
        <p className={styles.muted} aria-live="polite">Ждём ответа…</p>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <button type="button" className={styles.primary} onClick={() => setStep('code')}>Ввести код из SMS</button>
        <p className={styles.footer}>
          <button type="button" className={styles.link} onClick={onBack}>Вернуться ко входу</button>
        </p>
        <div ref={captchaBox} className={styles.captcha} />
      </div>
    )
  }

  if (step === 'code') {
    return (
      <form className={styles.form} onSubmit={submitRecoveryCode} noValidate>
        <h1 className={styles.title}>Введите код</h1>
        <p className={styles.subtitle}>
          Если учётная запись с номером {start?.phone ?? phone} есть, код уже в пути.
          {recState.channel === 'mobileid' ? ' Отправителем будет ваш оператор, а не Кличат.' : ''}
        </p>
        {testCode ? <p className={styles.testCode}>Тестовый сервер: код всегда {testCode}</p> : null}
        <Field label="Код из SMS" error={fields.code} htmlFor="rec-code">
          <CodeInput id="rec-code" value={code} onChange={setCode} length={recCodeLength} />
        </Field>
        <CodeFallback state={recState} />
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <button type="submit" className={styles.primary} disabled={busy || code.length !== recCodeLength}>Продолжить</button>
        <p className={styles.footer}>
          {resendIn > 0 ? (
            <span className={styles.muted}>Отправить ещё раз можно через {resendIn} с</span>
          ) : (
            <button type="button" className={styles.link} onClick={() => void sendCode()} disabled={busy}>Отправить код ещё раз</button>
          )}
          {' · '}
          <button type="button" className={styles.link} onClick={() => setStep('phone')}>Изменить номер</button>
        </p>
        <div ref={captchaBox} className={styles.captcha} />
      </form>
    )
  }

  return (
    <form className={styles.form} onSubmit={finish} noValidate>
      <h1 className={styles.title}>Новый пароль</h1>
      <p className={styles.subtitle}>После смены пароля все устройства будут разлогинены.</p>
      <Field label="Новый пароль" hint="Не короче 8 символов" error={fields.new_password ?? fields.password} htmlFor="rec-password">
        <input id="rec-password" className={styles.input} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
      </Field>
      {fields.code ? <p className={styles.error} role="alert">{fields.code} <button type="button" className={styles.link} onClick={() => setStep('code')}>Ввести код заново</button></p> : null}
      {error && !fields.code ? <p className={styles.error} role="alert">{error}</p> : null}
      <button type="submit" className={styles.primary} disabled={busy || password.length < 8}>
        {busy ? 'Сохраняем…' : 'Сменить пароль и войти'}
      </button>
    </form>
  )
}

export function Field({ label, hint, error, htmlFor, children }: { label: string; hint?: string | undefined; error?: string | undefined; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <label className={cx(styles.label, error && styles.labelError)} htmlFor={htmlFor}>
        {label}
        {error ? <span className={styles.labelErrorText}> — {error}</span> : null}
      </label>
      {children}
      {hint && !error ? <span className={styles.hint}>{hint}</span> : null}
    </div>
  )
}

/** Шесть ячеек кода: вставка целиком, автопереход, Backspace назад. */
/** Клеток столько, сколько цифр в коде: у наших SMS шесть, у мобильной авторизации четыре. */
export function CodeInput({ id, value, onChange, length = 6 }: { id: string; value: string; onChange: (v: string) => void; length?: number }) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(length, ' ').slice(0, length).split('')

  const set = (i: number, ch: string) => {
    const next = digits.slice()
    next[i] = ch || ' '
    onChange(next.join('').replace(/\s/g, ''))
  }

  return (
    <div className={styles.code} id={id}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          className={styles.codeCell}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={length}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Цифра ${i + 1}`}
          value={d.trim()}
          autoFocus={i === 0}
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, '')
            if (raw.length > 1) {
              onChange(raw.slice(0, length))
              refs.current[Math.min(raw.length, length - 1)]?.focus()
              return
            }
            set(i, raw)
            if (raw && i < length - 1) refs.current[i + 1]?.focus()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !d.trim() && i > 0) refs.current[i - 1]?.focus()
          }}
        />
      ))}
    </div>
  )
}

function maxBirthDate(): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 14)
  return d.toISOString().slice(0, 10)
}
